import React from 'react';
import { Card } from './ui/card';
import { Shirt, Coffee, Palette, Star, CheckCircle, CreditCard, Flag, Car, Zap, Camera } from 'lucide-react';
import { OrderButton } from './OrderButton';

export const Services = () => {
  const services = [
    {
      icon: Shirt,
      title: "Custom Apparel",
      description: "T-shirts, hoodies, hats and more with your custom design",
      features: ["Premium Quality Fabrics", "Moisture Wicking Options", "Durable Printing", "Comfortable Fit"],
      image: "https://customer-assets.emergentagent.com/job_brand-consultation/artifacts/uf4aa1l2_KNOWN-BRANDS-STARTED-HERE-AVID-BRANDING.png"
    },
    {
      icon: Coffee,
      title: "Custom Embroidery",
      description: "Professional embroidered hats, caps and accessories",
      features: ["High-Quality Materials", "Long-lasting Embroidery", "Various Colors", "Custom Designs"],
      image: "https://customer-assets.emergentagent.com/job_brand-consultation/artifacts/mg1vl23r_avidbranding-dot-ca.png"
    },
    {
      icon: Palette,
      title: "Brand Consultation",
      description: "Logo design and brand strategy for new businesses",
      features: ["Creative Design Process", "Brand Guidelines", "Logo Concepts", "Visual Identity"],
      image: "https://customer-assets.emergentagent.com/job_brand-consultation/artifacts/7w6v8wsm_brand-consultation-creation-avid-branding.png"
    },
    {
      icon: CreditCard,
      title: "Business Cards",
      description: "Professional business cards with custom designs and finishes",
      features: ["Premium Card Stock", "Multiple Finishes", "Full Color Printing", "Fast Turnaround"],
      image: "https://customer-assets.emergentagent.com/job_brand-consultation/artifacts/z6j2lcz1_business-cards-avid-branding-99-26-850-2590-quality-canadian-company-dufferin-county-mono.png"
    },
    {
      icon: Flag,
      title: "Custom Banners",
      description: "Large format printing for events, promotions, and signage",
      features: ["Weather Resistant", "Multiple Sizes", "Indoor & Outdoor", "Vibrant Colors"],
      image: "https://customer-assets.emergentagent.com/job_brand-consultation/artifacts/o8qlzp6t_custom-banner-avid-branding-226-850-2590-canadian-dufferin-county-mono.png"
    },
    {
      icon: Car,
      title: "Custom Spare Tire Covers",
      description: "Durable vinyl graphics for vehicles and outdoor equipment",
      features: ["Weather Proof", "Custom Graphics", "Perfect Fit", "UV Resistant"],
      image: "https://customer-assets.emergentagent.com/job_brand-consultation/artifacts/cnry15xz_custom-spare-tire-covers-2260850-2590-canada-dufferin-county.png"
    },
    {
      icon: Zap,
      title: "DTF Heat Transfers",
      description: "Direct-to-film transfers for fast, high-quality apparel printing",
      features: ["24 Hour Turnaround", "Gang Sheet Options", "Vibrant Colors", "Easy Application"],
      image: "https://customer-assets.emergentagent.com/job_brand-consultation/artifacts/vwjeqd5v_Custom-heat-transfers-in-24hrs-Orangeville-Mono.jpg",
      specialButton: true
    },
    {
      icon: Camera,
      title: "Brand Photography",
      description: "Authentic candid photography that captures your brand's true essence",
      features: ["Natural Lighting", "Candid Moments", "Social Media Ready", "Brand Storytelling"],
      image: "https://customer-assets.emergentagent.com/job_brand-consultation/artifacts/1luv9t1v_brand-photography-avid-branding-canadian-dufferin-county-mono.png"
    }
  ];

  const processes = [
    {
      step: "01",
      title: "Consultation",
      description: "Meet with our team to discuss your vision and requirements"
    },
    {
      step: "02", 
      title: "Design & Sample",
      description: "We create your design and provide a free sample for approval"
    },
    {
      step: "03",
      title: "Production",
      description: "Your order is carefully crafted with attention to detail"
    },
    {
      step: "04",
      title: "Delivery",
      description: "Quality checked products delivered on time, every time"
    }
  ];

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Our <span className="text-blue-600">Services</span>
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            From custom apparel to brand consultation, we provide comprehensive solutions 
            to help your business stand out and make a lasting impression.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-20">
          {services.map((service, index) => (
            <Card key={index} className="group hover:shadow-xl transition-all duration-300 overflow-hidden border-2 border-blue-100 hover:border-blue-300">
              <div className="relative">
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full aspect-square object-cover rounded-2xl transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <div className="bg-white p-2 rounded-lg">
                    <service.icon className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                
                <ul className="space-y-2 mb-4">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-gray-700">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Special button for DTF Heat Transfers */}
                {service.specialButton && (
                  <div className="mt-4">
                    <button
                      onClick={() => window.open('https://b2bheattransfersdufferincounty.myshopify.com/products/create-a-gang-sheet', '_blank')}
                      className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white py-3 px-4 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105"
                    >
                      🔥 Build a Gangsheet
                    </button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>

        {/* Process Section */}
        <div className="bg-gradient-to-r from-blue-50 to-sky-50 rounded-2xl p-8 lg:p-12">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Process</h3>
            <p className="text-lg text-gray-700">Simple, transparent, and focused on your success</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processes.map((process, index) => (
              <div key={index} className="text-center">
                <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {process.step}
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{process.title}</h4>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Quality Guarantee */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center bg-teal-100 text-teal-800 px-6 py-3 rounded-full font-semibold">
            <Star className="h-5 w-5 mr-2" />
            100% Quality Guarantee - No Minimum Orders
          </div>
          <div className="mt-8">
            <OrderButton
              label="Start Your Order"
              testid="services-start-order-button"
              className="px-10 py-5 text-lg"
            />
          </div>
        </div>
      </div>
    </section>
  );
};